import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "What is Endasphere?",
    a: "Endasphere is a global network and internet infrastructure provider that acts as a reverse proxy between users and your origin servers — enhancing the performance, security and reliability of websites, applications and APIs across 312 edge locations.",
  },
  {
    q: "How does Endasphere protect against malware and threats?",
    a: "Behavioral detection engines locate, quarantine and neutralize malware, DDoS bursts, credential-stuffing attacks and intrusions at the edge — before they reach your origin. Zero-day heuristics run in real time across the entire global network.",
  },
  {
    q: "Does Endasphere offer real-time game cheat detection?",
    a: "Yes. Endasphere uses sub-frame telemetry to detect aimbots, wallhacks and injectors the instant they load, enforcing fair play in real time for online multiplayer games.",
  },
  {
    q: "What is a reverse proxy CDN and why do I need one?",
    a: "A reverse proxy sits between your users and your origin servers — caching content, filtering malicious traffic and routing every request through the nearest edge node. This lowers latency, absorbs attacks and improves uptime and reliability.",
  },
  {
    q: "How fast and reliable is the Endasphere network?",
    a: "Endasphere operates 312 edge locations with a 99.99% uptime SLA and an average latency of 11ms, delivering fast, resilient connectivity worldwide.",
  },
];

function Item({ item, index, open, onToggle }) {
  return (
    <div className="border-b border-white/10" data-testid={`faq-item-${index}`}>
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-6 py-7 text-left group"
        aria-expanded={open}
      >
        <h3 className="font-display font-700 text-lg sm:text-xl lg:text-2xl tracking-tight text-white group-hover:text-[#22D3EE] transition-colors duration-300">
          {item.q}
        </h3>
        <span className={`flex-shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-colors duration-300 ${open ? "border-[#22D3EE] bg-[#22D3EE]/10" : "border-white/15"}`}>
          <Plus className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-45 text-[#22D3EE]" : "text-slate-400"}`} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-7 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-28 lg:py-40 bg-[#07070E] border-t border-white/10" data-testid="faq-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-4"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Answers</span>
          <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none">
            Frequently<br />asked
          </h2>
          <p className="mt-8 max-w-xs text-sm text-slate-400 leading-relaxed">
            Everything you need to know about security, connectivity and the sphere.
          </p>
        </motion.div>

        <div className="lg:col-span-8">
          {FAQS.map((item, i) => (
            <Item key={i} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}
